import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from '@env/environment';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Credentials, CredentialsService } from './credentials.service';
import { AnalyticsService } from '@app/ahoy-analytics/analytics.service';
import { RefreshTokenResponse } from '../models/refresh-token.model';

export const emailRegex =
  /^(?=.{1,256}$)[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,63}$/;

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private inFlightRefresh$: Observable<RefreshTokenResponse> | null = null;

  constructor(
    private httpClient: HttpClient,
    private credentialsService: CredentialsService,
    private analytics: AnalyticsService,
  ) {}

  /**
   * Refreshes the session. Refresh tokens are single-use on the backend, so
   * every caller (route guard, interceptor, parallel 401s) shares the one
   * in-flight request — a second refresh with the same token would be
   * rejected and log the user out.
   */
  refreshTokenAsync(): Observable<RefreshTokenResponse> {
    if (this.inFlightRefresh$) {
      return this.inFlightRefresh$;
    }

    const req = this.createRefreshTokenRequest().pipe(
      tap((res) => this.updateRefreshedToken(res)),
      catchError((err) => {
        if (this.isSessionRejected(err)) {
          this.logout().subscribe(() => {
            location.reload();
          });
        }
        return throwError(() => err);
      }),
      finalize(() => (this.inFlightRefresh$ = null)),
      shareReplay(1),
    );
    this.inFlightRefresh$ = req;

    // Fire it now so the new token is saved even if every caller unsubscribes.
    req.subscribe({ error: () => {} });

    return req;
  }

  /**
   * Only a definitive answer from the auth server ends the session. Network
   * drops (status 0 — common on Safari when the tab is backgrounded) and
   * server errors are transient; the next 401 will retry the refresh.
   */
  private isSessionRejected(err: unknown): boolean {
    if (!(err instanceof HttpErrorResponse)) return true;
    return err.status >= 400 && err.status < 500;
  }

  /**
   * The merchant/admin session is the only thing this refresh flow applies
   * to. Public, unauthenticated surfaces (e.g. the SMS tracking-link pages)
   * never have a refresh token and must not attempt this flow.
   */
  canRefreshToken(): boolean {
    return !!this.credentialsService.credentials?.refreshToken;
  }

  createRefreshTokenRequest(): Observable<RefreshTokenResponse> {
    if (
      this.credentialsService.credentials &&
      this.credentialsService.credentials.refreshToken
    ) {
      const url = '/org/refreshToken';
      if (environment.authType == 'Azure') {
        const body = new URLSearchParams();
        body.set(
          'refresh_token',
          this.credentialsService.credentials.refreshToken,
        );
        body.set('grant_type', 'refresh_token');

        body.set('client_id', environment.activeDirectoryClientId);
        body.set('scope', environment.activeDirectoryScope);

        body.set('response_type', 'token id_token');
        return this.httpClient.post<RefreshTokenResponse>(
          url,
          body.toString(),
          {
            headers: new HttpHeaders({
              'Content-Type': 'application/x-www-form-urlencoded',
            }),
          },
        );
      } else {
        const body = {
          refreshToken: this.credentialsService.credentials.refreshToken,
          clientId: environment.activeDirectoryClientId,
        };

        return this.httpClient
          .post<{
            accessToken: string;
            refreshToken: string;
            expireAt: number;
          }>(url, body, {
            headers: new HttpHeaders({
              'Content-Type': 'application/json',
            }),
          })
          .pipe(
            map((data) => {
              return {
                access_token: data.accessToken,
                refresh_token: data.refreshToken,
                expire_at: data.expireAt,
              };
            }),
          );
      }
    }
    return throwError(
      () => new Error('No refresh token available for the current session'),
    );
  }

  updateRefreshedToken(res: RefreshTokenResponse) {
    const data: Credentials = {
      companyId: this.credentialsService.credentials.companyId,
      username: this.credentialsService.credentials.username,
      token: res.access_token,
      refreshToken: res.refresh_token,
      companyName: this.credentialsService.credentials.companyName,
    };
    this.credentialsService.setCredentials(data);
  }

  /**
   * Logs out the user and clear credentials.
   * @return True if the user was logged out successfully.
   */
  logout(): Observable<boolean> {
    this.analytics.clearUser();
    // Customize credentials invalidation here
    this.credentialsService.setCredentials();
    return of(true);
  }
}
